/**
 * Forgotten-passphrase recovery (portal-spec §4.8). The user enters their 24-word
 * recovery code and a NEW passphrase; the client unwraps the UMK from the recovery
 * envelope and re-seals it under the new passphrase (items untouched, INV-14).
 * Errors are generic — no oracle about whether the code was valid (§6).
 *
 * "Wipe & restart" (lost the recovery code too) calls wipe/start; the confirmation link is
 * emailed server-side, so this screen only shows a "check your email" state (no token/link).
 */
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '../store/session';
import { isTooWeak, strength } from '../lib/passphrase';
import { Button, Card, ErrorNote, Field, Input, Textarea } from '../components/ui';

const WORD_COUNT = 24;

export function Recovery() {
  const recover = useSession((s) => s.recover);
  const cancelRecovery = useSession((s) => s.cancelRecovery);
  const wipeStart = useSession((s) => s.wipeStart);
  const busy = useSession((s) => s.busy);
  const serverError = useSession((s) => s.error);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showWipe, setShowWipe] = useState(false);
  const [wipeSent, setWipeSent] = useState(false);
  const [wipeBusy, setWipeBusy] = useState(false);

  const startWipe = (): void => {
    setWipeBusy(true);
    setLocalError(null);
    void wipeStart()
      // The confirmation link is emailed server-side; nothing about it reaches the client.
      .then(() => setWipeSent(true))
      .catch(() => setLocalError('Could not start a wipe. Try again.'))
      .finally(() => setWipeBusy(false));
  };

  const st = strength(pw);
  const words = code
    .trim()
    .split(/\s+/u)
    .filter((w) => w.length > 0);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    setLocalError(null);
    if (words.length !== WORD_COUNT) {
      setLocalError(`Enter all ${WORD_COUNT} words of your recovery code.`);
      return;
    }
    if (isTooWeak(pw)) {
      setLocalError('Choose a longer passphrase — at least 12 characters, with some variety.');
      return;
    }
    if (pw !== confirm) {
      setLocalError('Passphrases do not match.');
      return;
    }
    void recover(code, pw).then(() => {
      setCode('');
      setPw('');
      setConfirm('');
    });
  };

  if (wipeSent) {
    return (
      <div className="grid min-h-full place-items-center p-4">
        <Card className="w-full max-w-md space-y-4">
          <h1 className="text-xl font-semibold text-text">Check your email</h1>
          <p role="status" className="text-sm text-text-secondary">
            We sent a confirmation link to the email on your heivol-id account. Open it on this
            device to finish wiping your vault. The link works for 72 hours. If you did not ask for
            this, you can cancel the wipe from inside your vault.
          </p>
          <Button type="button" variant="ghost" onClick={cancelRecovery} className="w-full">
            Back to unlock
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid min-h-full place-items-center p-4">
      <Card className="w-full max-w-md space-y-5">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-text">Recover your vault</h1>
          <p className="text-sm text-text-secondary">
            Enter the 24-word recovery code you printed at setup, then choose a new passphrase. This
            is the only way back in — we cannot reset your passphrase for you.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Recovery code" hint={`${words.length}/${WORD_COUNT} words`}>
            <Textarea
              rows={3}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </Field>

          <Field label="New vault passphrase" hint="Different from your heivol-id password.">
            <Input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />
          </Field>

          {pw.length > 0 && (
            <div className="space-y-1">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
                <div
                  className={`h-full transition-all ${st.score >= 3 ? 'bg-success' : st.score >= 2 ? 'bg-warning' : 'bg-error'}`}
                  style={{ width: `${Math.min(100, (st.score / 5) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-text-secondary">Strength: {st.label}</p>
            </div>
          )}

          <Field label="Confirm new passphrase">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>

          {localError !== null && <ErrorNote>{localError}</ErrorNote>}
          {serverError !== null && <ErrorNote>{serverError}</ErrorNote>}

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Recovering your vault…' : 'Recover and set new passphrase'}
          </Button>

          <button
            type="button"
            onClick={cancelRecovery}
            disabled={busy}
            className="w-full text-center text-xs text-text-secondary underline underline-offset-2 hover:text-text disabled:opacity-50"
          >
            Back to unlock
          </button>
        </form>

        <div className="space-y-2 border-t border-white/10 pt-4">
          {!showWipe ? (
            <button
              type="button"
              onClick={() => setShowWipe(true)}
              className="w-full text-center text-xs text-text-secondary underline underline-offset-2 hover:text-text"
            >
              Lost your recovery code too?
            </button>
          ) : (
            <div className="space-y-3 rounded-md bg-surface-alt p-3">
              <p className="text-xs text-text-secondary">
                If you&rsquo;ve lost both your passphrase and your recovery code, your items
                can&rsquo;t be decrypted by anyone — including us. You can wipe the vault and start
                fresh with a new passphrase. Your heivol-id login stays.
              </p>
              <Button
                type="button"
                variant="danger"
                onClick={startWipe}
                disabled={wipeBusy}
                className="w-full"
              >
                {wipeBusy ? 'Sending email…' : 'Wipe & restart'}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
